import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { showToast } from "@/components/ui/toast";
import { PROTECTED_ROUTES } from "@/constants/routes";
import { PriceInput } from "@/features/listings/components/PriceInput";
import { WizardActionBar } from "@/features/listings/components/WizardActionBar";
import { useSellWizard } from "@/features/listings/context/SellWizardContext";
import { useCreateListing } from "@/features/listings/hooks/useCreateListing";
import {
  finalizeStepSchema,
  type FinalizeStepValues,
} from "@/features/listings/schemas/listing.schemas";
import { toListingFormData } from "@/features/listings/types/listing";
import { ApiError } from "@/types/api";

/** docs/ui/routes.md `/sell/finalize` — Create Listing Step 3 (Pricing & Location). */
export function SellFinalizePage() {
  const navigate = useNavigate();
  const { draft, updateDraft } = useSellWizard();
  const createListingMutation = useCreateListing();

  useEffect(() => {
    if (draft.images.length === 0) {
      navigate(PROTECTED_ROUTES.sellMedia, { replace: true });
    } else if (!draft.title) {
      navigate(PROTECTED_ROUTES.sellDetails, { replace: true });
    }
  }, [draft.images.length, draft.title, navigate]);

  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FinalizeStepValues>({
    resolver: zodResolver(finalizeStepSchema),
    defaultValues: {
      price: draft.price,
      originalPrice: draft.originalPrice,
      negotiable: draft.negotiable,
      city: draft.city,
      state: draft.state,
      tags: draft.tags,
    },
  });

  function publish(values: FinalizeStepValues) {
    const merged = { ...draft, ...values };
    updateDraft(values);
    createListingMutation.mutate(toListingFormData(merged, "active"), {
      onSuccess: () => {
        showToast.success("Your listing is live.");
        navigate(PROTECTED_ROUTES.myListings);
      },
      onError: (error) => {
        showToast.error(error instanceof ApiError ? error.message : "Couldn't publish listing.");
      },
    });
  }

  function saveDraft() {
    // Save Draft only needs `price`/`city`/`state` to be well-formed *if*
    // filled in — it deliberately skips full-form validation (that's the
    // difference from Publish), so an incomplete draft can still be saved.
    const values = getValues();
    const merged = { ...draft, ...values };
    updateDraft(values);
    createListingMutation.mutate(toListingFormData(merged, "draft"), {
      onSuccess: () => {
        showToast.success("Draft saved.");
        navigate(PROTECTED_ROUTES.myListings);
      },
      onError: (error) => {
        showToast.error(error instanceof ApiError ? error.message : "Couldn't save draft.");
      },
    });
  }

  return (
    <form onSubmit={handleSubmit(publish)} noValidate className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-h2 text-foreground">Set your price and location</h1>
        <p className="text-body text-muted-foreground">
          Buyers in your area will see this listing first.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="price">Price</Label>
        <PriceInput id="price" {...register("price")} errorText={errors.price?.message} />
        {errors.price && (
          <p className="text-caption text-destructive" role="alert">
            {errors.price.message}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="originalPrice">Original price (optional)</Label>
        <PriceInput id="originalPrice" {...register("originalPrice")} />
        {errors.originalPrice && (
          <p className="text-caption text-destructive" role="alert">
            {errors.originalPrice.message}
          </p>
        )}
      </div>

      <Controller
        name="negotiable"
        control={control}
        render={({ field }) => (
          <div className="flex items-center gap-2">
            <Checkbox
              id="negotiable"
              checked={field.value}
              onCheckedChange={(checked) => field.onChange(checked === true)}
            />
            <Label htmlFor="negotiable" className="font-normal">
              Price is negotiable
            </Label>
          </div>
        )}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="city">City</Label>
          <Input id="city" {...register("city")} />
          {errors.city && (
            <p className="text-caption text-destructive" role="alert">
              {errors.city.message}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="state">State</Label>
          <Input id="state" {...register("state")} />
          {errors.state && (
            <p className="text-caption text-destructive" role="alert">
              {errors.state.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="tags">Tags (optional, comma-separated)</Label>
        <Input id="tags" placeholder="e.g. mobile, apple, smartphone" {...register("tags")} />
      </div>

      <WizardActionBar
        onBack={() => navigate(PROTECTED_ROUTES.sellDetails)}
        primaryLabel="Publish"
        onPrimary={handleSubmit(publish)}
        isPrimaryLoading={createListingMutation.isPending}
        secondaryLabel="Save Draft"
        onSecondary={saveDraft}
        isSecondaryLoading={createListingMutation.isPending}
      />
    </form>
  );
}
